// @flow
import React, {useState, Fragment} from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from '@material-ui/core/styles';
import {Typography, Divider, Chip} from '@material-ui/core';
import {Link, Route, Switch, useHistory} from 'react-router-dom';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import NewPaymentMethodContainer from '../containers/NewPaymentMethodContainer';
import SnackBar from '../../../../elements/SnackBar/SnackBar';
import {colors} from '../../../../assets/styles/colors';
import {useEffect} from 'react';
import ConfigureToggledButton from '../../components/ConfigureToggledButton';
import {AddCircleOutlineTwoTone} from '@material-ui/icons';
import {useIsFeatureInTier} from '../../../../utils/component';

import {usePage} from '../../../page/utils';
import MercadoPagoConfigContainer from '../containers/MercadoPagoConfigContainer';
import analytics from '../../../../analytics';
import queryString from 'query-string';

import iziPayLogo from '../../../../assets/img/payment-methods/izipay-logo.png';
import mercadoPagoLogo from '../../../../assets/img/payment-methods/mercadopago-logo.png';
import payOnDeliveryIcon from '../../../../assets/img/payment-methods/pay-on-delivery-icon.png';
import culqiLogo from '../../../../assets/img/payment-methods/culqi-logo.webp';
import stripeLogo from '../../../../assets/img/payment-methods/stripe-logo.webp';
import paypalLogo from '../../../../assets/img/payment-methods/paypal-logo.webp';
import transbankLogo from '../../../../assets/img/payment-methods/transbank-logo.webp';
import niubizLogo from '../../../../assets/img/payment-methods/niubiz-logo.webp';
import {useIsMinimalPlan, useStoreMetadata} from '../../../store/utils';
import {useMinimalTierContext} from '../../../../context/MinimalProvider';
import PaymentMethodDisabledModal from './Modals/PaymentMethodDisabledModal';

import type {Store, PaymentMethod, Currency} from '../../../../entities';
import {methodsByCurrency, universalMethods} from '../utils/paymentData';

type Props = {|
  store: Store,
  isLoading?: boolean,
  error?: string,
  deleteSuccess?: boolean,
  addSuccess?: boolean,
  updateSuccess?: boolean,
  paymentMethods: PaymentMethod[],
  currency: Currency,
  resetState: () => void,
  resetPartialState: () => void,
  getPaymentMethods: () => void,
|};

const useStyles = makeStyles(theme => ({
  title: {
    fontWeight: 600,
    padding: theme.spacing(2, 0, 1, 0),
    textAlign: 'left',
  },
  subtitle: {
    fontWeight: 500,
    padding: theme.spacing(0, 0, 2, 0),
    textAlign: 'left',
  },
  divider: {
    background: colors.grey200,
  },
  cardPayment: {
    display: 'flex',
    justifyContent: 'flex-start',
    flexDirection: 'column',
    padding: theme.spacing(0, 5),
    margin: theme.spacing(2, 0),
  },
  root: {
    marginTop: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(0, 3),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(0, 2),
    },
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    backgroundColor: 'white',
    borderRadius: '12px',
    overflow: 'hidden',
  },
  label: {
    fontWeight: 500,
    fontSize: '1rem',
    color: colors.grey800,
    '&.disabled': {
      color: colors.grey500,
    },
  },
  secondLine: {
    color: colors.grey800,
    fontSize: '0.75rem',
    '&.disabled': {
      color: colors.grey500,
    },
  },
  newPayment: {
    margin: theme.spacing(0, 1),
    borderRadius: '0px',
    padding: theme.spacing(2),
    textAlign: 'left',
    justifyContent: 'flex-start',
    fontWeight: 500,
    fontSize: '1rem',
    width: '100%',
    borderStyle: 'dashed',
    borderWidth: '1px',
    color: colors.grey800,
    borderColor: colors.grey500,
    '&.disabled': {
      color: colors.grey500,
      borderColor: colors.grey200,
    },
  },
  endIcon: {
    marginLeft: 'auto',
    color: colors.purple3,
    '&.disabled': {
      color: colors.grey500,
      borderColor: colors.grey200,
    },
  },
  saveAlert: {
    // width: '100%',
    marginTop: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      marginTop: theme.spacing(2),
    },
  },
  snackBarSuccess: {
    backgroundColor: '#E5F7F2',
    color: '#00B282',
  },
  snackBarError: {
    backgroundColor: '#FDECEA',
    color: '#A05536',
  },
  newLabel: {
    background: '#F5F0FF',
    marginLeft: 10,
    fontWeight: 600,
    '& span': {
      color: '#7B39FF',
    },
  },
  createPaymentLink: {
    fontSize: '1rem',
    color: colors.grey800,
    margin: theme.spacing(2, 7),
    [theme.breakpoints.down('sm')]: {
      marginLeft: theme.spacing(3),
    },
  },
  createPaymentLinkLink: {
    // color: colors.grey800,
    fontSize: '0.875rem',
    fontWeight: 600,
  },
}));

export default usePage<Props>(Payment, 'Payment Method Configuration');

type PaymentMethodAttributes = {
  displayName: string,
  secondLine: string,
  defaultImage?: string,
  id: string,
  chipMessage?: string,
};

const isUniversalMethod = (method: PaymentMethod) => {
  return universalMethods.includes(method.paymentProcessor.code);
};

const isCurrencySpecificMethod = (
  method: PaymentMethod,
  currency: Currency
) => {
  const supportedCurrencies = methodsByCurrency[method.paymentProcessor.code];
  return supportedCurrencies && supportedCurrencies.includes(currency.code);
};

const shouldDisplayMethod = (method: PaymentMethod, currency: Currency) => {
  if (method.enabled) return true;
  if (!currency) return isUniversalMethod(method);
  return (
    isUniversalMethod(method) || isCurrencySpecificMethod(method, currency)
  );
};

function Payment(props: Props) {
  const classes = useStyles();
  const {
    store,
    isLoading,
    error,
    deleteSuccess,
    addSuccess,
    updateSuccess,
    paymentMethods,
    currency,
    resetPartialState,
    // getPaymentMethods,
    // resetState,
  } = props;

  const storeMetadata = useStoreMetadata();

  const disabledContinueToChatPaymentMethods = storeMetadata
    ? !storeMetadata?.featureRestrictions?.paymentMethod.CONTINUE_TO_CHAT
    : undefined;

  const isMinimal = useIsMinimalPlan();
  const {
    openRestriction,
    handleOpenRestriction,
    handleCloseRestriction,
  } = useMinimalTierContext();

  const query = queryString.parse(window.location.search);
  const history = useHistory();

  const hasPaymentInformationFeature = useIsFeatureInTier(
    'MY_PAYMENT_INFORMATION'
  );
  const hasPaymentProcessorFeature = useIsFeatureInTier('PAYMENT_PROCESSOR');
  const hasMercadopagoCheckoutProIntegrationFeature = useIsFeatureInTier(
    'MERCADOPAGO_CHECKOUT_PRO_INTEGRATION'
  );

  const hasMercadoPagoFeature = useIsFeatureInTier(
    'MERCADOPAGO_CHECKOUT_PRO_INTEGRATION'
  );

  const [tierRestricted, setTierRestricted] = useState(false);

  const handleOnClick = () => {
    //NOTE: This handle set the state values: isLoading, deleteSuccess, updateSuccess and addSuccess
    // in false and undefined in order to clean the flags used in the NewPaymentMethod
    resetPartialState();
  };

  const hasDeliveryOptionsFeature = useIsFeatureInTier('DELIVERY_OPTIONS');

  useEffect(() => {
    if (!hasDeliveryOptionsFeature) {
      setTierRestricted(true);
    }
  }, [hasDeliveryOptionsFeature, isMinimal, props, store]);

  useEffect(() => {
    handleOpenRestriction();
  }, [handleOpenRestriction]);

  const NewPaymentMethodContainerButtom =
    hasPaymentProcessorFeature &&
    hasMercadopagoCheckoutProIntegrationFeature ? (
      <Fragment>
        <CustomButton
          classes={{
            root: classes.newPayment,
            endIcon: classes.endIcon,
          }}
          color="default"
          disabled={isLoading || tierRestricted}
          endIcon={
            <AddCircleOutlineTwoTone
              className={`${classes.endIcon} ${
                isLoading || tierRestricted ? 'disabled' : ''
              }`}
              style={{padding: '1px'}}
              color="primary"
            />
          }
          component={Link}
          onClick={handleOnClick}
          to={`/configure/payment-methods/new-method${window.location.search}`}
        >
          <div>
            <Typography
              className={`${classes.label} ${
                isLoading || tierRestricted ? 'disabled' : ''
              }`}
              component="span"
              id="add-new-payment-method"
            >
              Añadir Nuevo
              {!hasPaymentInformationFeature && (
                /*<StarRounded style={{marginLeft: 12, color: '#FFC107'}} />*/
                <Chip label="PRO" size="small" className={classes.newLabel} />
              )}
            </Typography>

            <Typography
              className={`${classes.secondLine} ${
                isLoading || tierRestricted ? 'disabled' : ''
              }`}
            >
              Transferencias bancarias, Yape, otros.
            </Typography>
          </div>
        </CustomButton>
        <br />

        <Divider className={classes.divider} />
      </Fragment>
    ) : null;

  const disabledButton = route => {
    let disabled: boolean;

    if (route.disabled || tierRestricted) {
      disabled = true;
    }
    if (
      // store.storeTier.name < 'TIER_3' &&
      route.paymentMethod === 'CONTINUE_TO_CHAT'
    ) {
      disabled = false;
    }

    return disabled;
  };

  useEffect(() => {
    return () => resetPartialState();
  }, [resetPartialState]);

  // useEffect(() => {
  //   getPaymentMethods();
  // }, [getPaymentMethods]);

  // useEffect(() => () => resetState(), [resetState]);

  useEffect(() => {
    if (addSuccess || updateSuccess) {
      analytics.fireCustomEvent(
        'Payment Method Configured',
        undefined,
        ['user-guiding'],
        () => {
          if (query.fromOnboarding) {
            resetPartialState();
            history.push('/home');
          }
        }
      );
    }
  }, [
    addSuccess,
    history,
    query.fromOnboarding,
    resetPartialState,
    updateSuccess,
  ]);

  if (isLoading) return null;

  const mapPaymentMethodAttributes = (
    method: PaymentMethod
  ): PaymentMethodAttributes => {
    if (method.disabled) {
      return {
        displayName: method.displayName,
        secondLine: 'Pronto estará disponible',
        id: '',
      };
    }

    if (method.paymentProcessor.code === 'CONTINUE_TO_CHAT') {
      return {
        displayName: 'Pedir por WhatsApp',
        secondLine:
          'Integración con WhatsApp. Se recomienda mantener activa esta opción.',
        id: 'payment-method-whatsapp',
      };
    }

    if (method.paymentProcessor.code === 'MERCADOPAGO_CHECKOUT_PRO') {
      return {
        displayName: 'Mercado Pago',
        secondLine: 'Acepta pagos con tarjeta de crédito/débito.',
        defaultImage: mercadoPagoLogo,
        id: 'payment-method-mercadopago',
        chipMessage: hasMercadoPagoFeature ? 'NUEVO' : 'Mejora tu plan',
      };
    }

    if (method.paymentProcessor.code === 'IZIPAY') {
      return {
        displayName: 'IziPay',
        secondLine: 'Acepta pagos con tarjeta de crédito/débito.',
        defaultImage: iziPayLogo,
        id: 'payment-method-izipay',
        chipMessage: 'NUEVO',
      };
    }

    if (method.paymentProcessor.code === 'CULQI') {
      return {
        displayName: 'Culqi',
        secondLine: 'Acepta pagos con tarjeta de crédito/débito.',
        defaultImage: culqiLogo,
        id: 'payment-method-culqi',
        chipMessage: 'NUEVO',
      };
    }

    if (method.paymentProcessor.code === 'STRIPE_CHECKOUT') {
      return {
        displayName: 'Stripe',
        secondLine: 'Acepta pagos con tarjeta de crédito/débito.',
        defaultImage: stripeLogo,
        id: 'payment-method-stripe',
        chipMessage: 'NUEVO',
      };
    }

    if (method.paymentProcessor.code === 'PAYPAL') {
      return {
        displayName: 'Paypal',
        secondLine: 'Acepta pagos con tarjeta de crédito/débito.',
        defaultImage: paypalLogo,
        id: 'payment-method-paypal',
        chipMessage: 'NUEVO',
      };
    }

    if (method.paymentProcessor.code === 'TRANSBANK') {
      return {
        displayName: 'Transbank',
        secondLine: 'Acepta pagos con tarjeta de crédito/débito.',
        defaultImage: transbankLogo,
        id: 'payment-method-transbank',
        chipMessage: 'NUEVO',
      };
    }

    if (method.paymentProcessor.code === 'NIUBIZ_WEB') {
      return {
        displayName: 'Niubiz Web',
        secondLine: 'Acepta pagos con tarjeta de crédito/débito.',
        defaultImage: niubizLogo,
        id: 'payment-method-niubiz',
        chipMessage: 'NUEVO',
      };
    }

    if (method.paymentProcessor.code === 'NIUBIZ_PAY_LINK') {
      return {
        displayName: 'Niubiz Pay Link',
        secondLine: 'Acepta pagos con tarjeta de crédito/débito.',
        defaultImage: niubizLogo,
        id: 'payment-method-niubiz',
        chipMessage: 'NUEVO',
      };
    }

    if (method.paymentProcessor.code === 'PAY_ON_DELIVERY') {
      return {
        displayName: 'Pago contra entrega',
        secondLine:
          'Recibe los pedidos y coordina la entrega y el pago directamente al entregar el producto.',
        defaultImage: payOnDeliveryIcon,
        id: 'payment-method-pay-on-delivery',
        isNew: false,
      };
    }

    return {
      displayName: method.displayName,
      secondLine: '',
      id: 'payment-method-others',
      isNew: false,
    };
  };

  return (
    <div className={classes.root}>
      <PaymentMethodDisabledModal
        open={openRestriction}
        handleClose={handleCloseRestriction}
      />
      <SnackBar
        mode="success"
        className={classes.snackBarSuccess}
        message={
          deleteSuccess
            ? 'Se eliminó el método de pago exitosamente'
            : undefined
        }
      />

      <SnackBar
        mode="error"
        className={classes.snackBarError}
        message={
          error === 'Invalid auth keys' || error === 'Unhandled error'
            ? 'Verifique que sus credenciales sean correctas'
            : error
        }
      />

      <SnackBar
        mode="success"
        className={classes.snackBarSuccess}
        message={
          addSuccess ? 'Se creó el método de pago exitosamente' : undefined
        }
      />
      <SnackBar
        mode="success"
        className={classes.snackBarSuccess}
        message={
          updateSuccess ? 'Se guardaron los cambios exitosamente' : undefined
        }
      />
      <Switch>
        <Route exact path="/configure/payment-methods">
          <Typography component={'span'} variant="h6" className={classes.title}>
            Elige los métodos de pagos que desee aceptar en su tienda
          </Typography>
          <Typography
            component={'span'}
            variant="body2"
            className={classes.subtitle}
          >
            (TiendaDA no cobra comisiones de venta. Para el pago con tarjetas de
            crédito, su proveedor de pasarela cobrará el % que haya negociado
            con usted)
          </Typography>
          {paymentMethods
            ?.filter(method => shouldDisplayMethod(method, currency))
            .filter(method => {
              if (
                disabledContinueToChatPaymentMethods &&
                method.paymentMethod === 'CONTINUE_TO_CHAT'
              ) {
                return false;
              }
              return true;
            })
            .map((method, idx) => {
              const {
                displayName,
                secondLine,
                defaultImage,
                id,
                chipMessage,
              } = mapPaymentMethodAttributes(method);

              return (
                <Fragment key={`logisticsButton-${idx}`}>
                  <ConfigureToggledButton
                    enabled={method.enabled}
                    disabled={disabledButton(method)}
                    path={`/configure/payment-methods/${method.id}${window.location.search}`}
                    label={
                      <div
                        style={{display: 'flex', alignItems: 'center'}}
                        id={id}
                      >
                        {displayName}
                        {chipMessage && (
                          <Chip
                            label={chipMessage}
                            size="small"
                            className={classes.newLabel}
                          />
                        )}
                      </div>
                    }
                    img={defaultImage ?? undefined}
                    secondLine={secondLine}
                    onClick={handleOnClick}
                  />
                  {idx < paymentMethods.length && (
                    <Divider className={classes.divider} />
                  )}
                </Fragment>
              );
            })}

          {NewPaymentMethodContainerButtom}
          {/* <ConfigureToggledButton
              // enabled={route.enabled}
              // disabled={disabledButton(route)}
              path={`/configure/payment-methods/payment-processor`}
              label="Pagos con Tarjeta"
              // secondLine={
              //   route.disabled ? 'Pronto estará disponible' : undefined
              // }
              onClick={handleOnClick}
            /> */}
        </Route>
        <Route
          path={'/configure/payment-methods/new-method'}
          component={tierRestricted ? () => null : NewPaymentMethodContainer}
        />
        <Route
          path={'/configure/payment-methods/mercado-pago'}
          component={tierRestricted ? () => null : MercadoPagoConfigContainer}
        />
        {paymentMethods &&
          paymentMethods.map((route: PaymentMethod, idx: number) => (
            <Route
              key={`payment-route-${idx}`}
              path={`/configure/payment-methods/:id`}
              component={
                tierRestricted && route.paymentMethod !== 'CONTINUE_TO_CHAT'
                  ? () => null
                  : NewPaymentMethodContainer
              }
            />
          ))}
      </Switch>
    </div>
  );
}

Payment.propTypes = {
  store: PropTypes.object.isRequired,
  isLoading: PropTypes.bool,
  deleteSuccess: PropTypes.bool,
  addSuccess: PropTypes.bool,
  updateSuccess: PropTypes.bool,
  error: PropTypes.string,
  paymentMethods: PropTypes.array,
  getPaymentMethods: PropTypes.func.isRequired,
  resetState: PropTypes.func.isRequired,
  resetPartialState: PropTypes.func.isRequired,
};
